import { useCallback } from "react";

/**
 * A custom hook that merges multiple React refs into a single ref function.
 * This is useful when you need to apply multiple refs to a single element,
 * such as when combining a forwarded ref with internal refs.
 *
 * @param refs - Array of React refs to merge
 * @returns A callback ref function that applies all provided refs to the element
 */
export function useMergeRefs<T = unknown>(
  ...refs: Array<React.Ref<T> | undefined>
) {
  return useCallback(
    (value: T) => {
      refs.forEach((ref) => {
        if (!ref) return;

        if (typeof ref === "function") {
          ref(value);
        } else {
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions, no-param-reassign
          (ref as React.MutableRefObject<T>).current = value;
        }
      });
    },
    [refs],
  );
}
